import { useState, type FormEvent } from 'react';
import { useReviewFinding } from '../../api/queries';
import type { FindingDetail, ReviewDecision } from '../../api/types';
import { Field, MutationError } from '../../components/Form';
import { localToIso } from '../../lib/events';
import { annotationKindMeta, decisionMeta } from '../../lib/format';
import { ReviewEditor, diffDraft, draftFrom, evidenceFromDraft, type FindingDraft } from './ReviewEditor';

const DECISIONS = Object.keys(decisionMeta) as ReviewDecision[];

const decisionHelp: Record<ReviewDecision, string> = {
  publish: 'The evidence supports this wording. Creates a reviewed version that exports can use.',
  contest: 'A disagreement remains unresolved. The finding stays visible, clearly marked as contested.',
  supersede: 'Replaced by a later finding or no longer current. Kept for history.',
};

const MIN_REASON = 12;

interface Errors {
  decision?: string;
  reason?: string;
  draft?: string;
}

/**
 * A steward decision always carries a reason and the version it was made
 * against, so two stewards cannot silently overwrite each other. Any
 * substantive field, including evidence and pinned source versions, can be
 * corrected in the same decision.
 */
export function ReviewPanel({ detail }: { detail: FindingDetail }) {
  const { finding } = detail;
  const mutation = useReviewFinding(finding.id);
  const [decision, setDecision] = useState<ReviewDecision | null>(null);
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FindingDraft>(() => draftFrom(finding));
  const [baseVersion, setBaseVersion] = useState(finding.version);
  const [errors, setErrors] = useState<Errors>({});
  const [done, setDone] = useState<string | null>(null);

  // A new current version (ours or another steward's) resets the draft to it.
  if (baseVersion !== finding.version) {
    setBaseVersion(finding.version);
    setDraft(draftFrom(finding));
    setEditing(false);
  }

  const changes = editing ? diffDraft(finding, draft) : {};
  const changed = Object.keys(changes);
  const evidenceCount = editing ? evidenceFromDraft(draft.evidence).length : finding.evidence.length;
  const openItems = detail.annotations.filter((annotation) => annotation.findingVersion === finding.version && annotation.kind !== 'context');

  function submit(event: FormEvent) {
    event.preventDefault();
    const next: Errors = {};
    if (decision === null) next.decision = 'Choose a decision.';
    if (reason.trim().length < MIN_REASON) next.reason = `Give a reason of at least ${String(MIN_REASON)} characters. It is shown to members.`;
    if (decision === 'publish' && evidenceCount === 0) next.decision = 'A finding without evidence cannot be published.';
    if (editing && draft.title.trim() === '') next.draft = 'The title cannot be empty.';
    else if (editing && draft.method.trim() === '') next.draft = 'The method cannot be empty.';
    else if (editing && localToIso(draft.sourceCutoff) === null) next.draft = 'Enter a valid source cutoff.';
    setErrors(next);
    if (Object.keys(next).length > 0 || decision === null) return;
    setDone(null);
    mutation.mutate(
      { expectedVersion: finding.version, decision, reason: reason.trim(), ...(changed.length > 0 ? { changes } : {}) },
      {
        onSuccess: (result) => {
          setDone(`${decisionMeta[decision].verb}. Version ${String(result.finding.version)} is now current.`);
          setReason('');
          setDecision(null);
        },
      },
    );
  }

  return (
    <form className="panel panel--steward form" onSubmit={submit} aria-labelledby="review-form-title" noValidate>
      <h3 id="review-form-title" className="panel__title">
        Decide on version {finding.version}
      </h3>
      {openItems.length > 0 ? (
        <div className="review-open">
          <p>
            <strong>{openItems.length}</strong> member {openItems.length === 1 ? 'response is' : 'responses are'} waiting on this version:
          </p>
          <ul>
            {openItems.map((annotation) => (
              <li key={annotation.id}>
                <span className={`annotation__kind annotation__kind--${annotation.kind}`}>{annotationKindMeta[annotation.kind].noun}</span>{' '}
                {annotation.body.length > 140 ? `${annotation.body.slice(0, 140)}…` : annotation.body}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="muted">No corrections, challenges or proposals are waiting on this version.</p>
      )}

      <fieldset className="choice-group" aria-describedby={errors.decision ? 'decision-error' : undefined}>
        <legend className="field__label">Decision</legend>
        {DECISIONS.map((option) => (
          <label key={option} className={`choice${decision === option ? ' choice--selected' : ''}`}>
            <input type="radio" name="review-decision" value={option} checked={decision === option} onChange={() => setDecision(option)} />
            <span className="choice__text">
              <span className="choice__label">{decisionMeta[option].label}</span>
              <span className="choice__help">{decisionHelp[option]}</span>
            </span>
          </label>
        ))}
        {errors.decision && (
          <p className="field__error" id="decision-error">
            {errors.decision}
          </p>
        )}
      </fieldset>

      <Field label="Reason" error={errors.reason ?? null} help="Recorded with the decision and shown in the finding’s history.">
        {(props) => <textarea {...props} className="input textarea" rows={4} value={reason} onChange={(event) => setReason(event.target.value)} />}
      </Field>

      <div className="disclosure">
        <button
          type="button"
          className="button button--secondary"
          aria-expanded={editing}
          onClick={() => {
            if (editing) setDraft(draftFrom(finding));
            setEditing(!editing);
          }}
        >
          {editing ? 'Discard revisions' : 'Revise this version'}
        </button>
        {editing && (
          <div className="disclosure__body">
            <p className="small muted">
              Open a section to change it. Only the fields you change are sent; everything else carries over from version{' '}
              {finding.version}.
            </p>
            <ReviewEditor draft={draft} onChange={setDraft} />
            <p className="small" role="status">
              {changed.length === 0 ? 'No fields changed yet.' : `Changing: ${changed.join(', ')}.`}
            </p>
            {errors.draft && <p className="field__error">{errors.draft}</p>}
          </div>
        )}
      </div>

      <MutationError
        error={mutation.error}
        conflictMessage="Another steward decided on this finding first. The latest version has been loaded; your reason is kept — review it again before deciding."
      />
      {done !== null && (
        <p className="form-success" role="status">
          {done}
        </p>
      )}
      <div className="button-row">
        <button type="submit" className="button button--primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Recording…' : 'Record decision'}
        </button>
      </div>
    </form>
  );
}
